import moment from 'moment';

import { PersonFilter } from '~/types/api.graphql';
import { pulseTopicOptionLabel } from '~/utils/pulse';
import PersonFilterHelper from '~/views/audiences/AudiencePage/Tabs/OverviewPage/helpers/PersonFilterHelper';

import { ConfirmationTranslate, translateEnumOption, translateFieldLabel } from './confirmationCopy';
import {
  MutationConfirmation,
  MutationConfirmationFieldClassification,
  MutationConfirmationSchemaProperty,
} from './mutationConfirmation.types';

export interface CardFieldOption {
  value: string;
  label: string;
}

export interface CardField {
  name: string;
  label: string;
  value: unknown;
  required: boolean;
  sensitive: boolean;
  fixed: boolean;
  display?: string;
  schemaType?: string;
  stringArray: boolean;
  /** The values a single-value enum input accepts, with their labels; it renders as a single-choice dropdown. */
  enumOptions?: CardFieldOption[];
}

export const buildFieldList = (confirmation: MutationConfirmation, translate?: ConfirmationTranslate): CardField[] => {
  const properties = confirmation.schema?.properties ?? {};
  const classifications = confirmation.fields ?? {};
  const values = confirmation.values ?? {};
  const required = new Set(confirmation.schema?.required ?? []);
  const definitions = confirmation.schema?.$defs ?? {};

  const names = Object.keys(properties).length
    ? Object.keys(properties)
    : [...new Set([...Object.keys(classifications), ...Object.keys(values)])];

  return names
    .filter((name) => !classifications[name]?.systemProvided)
    .filter((name) => !(classifications[name]?.fixed && classifications[name]?.hidden))
    .filter(
      (name) =>
        !isUnproposedOptionalField({
          name,
          properties,
          required,
          classification: classifications[name],
          value: values[name],
        }),
    )
    .map((name) => ({
      name,
      label: resolveLabel(name, confirmation.tool, classifications[name], properties[name], translate),
      value: values[name],
      required: required.has(name),
      sensitive: !!classifications[name]?.sensitive,
      fixed: !!classifications[name]?.fixed,
      display: classifications[name]?.display,
      schemaType: resolveSchemaType(properties[name]),
      stringArray: isStringArraySchema(properties[name]),
      enumOptions: resolveEnumOptions(properties[name], definitions, translate),
    }));
};

const DEFINITIONS_REF_PREFIX = '#/$defs/';

/**
 * The options of a single-value enum input, labelled with the product's copy. Mirrors the agent's
 * `resolve_enum_values` (a2ui/surfaces.py): the enum is a `$defs` entry the property points to with
 * `$ref`, wrapped in `anyOf` with `null` when optional. A list of enum values is typed `array`, so it
 * isn't one.
 */
const resolveEnumOptions = (
  property: MutationConfirmationSchemaProperty | undefined,
  definitions: Record<string, MutationConfirmationSchemaProperty>,
  translate: ConfirmationTranslate | undefined,
): CardFieldOption[] | undefined => {
  const resolved = resolveEnum(property, definitions);
  if (!resolved) return undefined;
  return resolved.values.map((value) => ({
    value,
    label: translate && resolved.name ? translateEnumOption(translate, resolved.name, value) : value,
  }));
};

const resolveEnum = (
  property: MutationConfirmationSchemaProperty | undefined,
  definitions: Record<string, MutationConfirmationSchemaProperty>,
  name?: string,
): { name?: string; values: string[] } | undefined => {
  if (!property) return undefined;
  const ref = property.$ref;
  if (typeof ref === 'string' && ref.startsWith(DEFINITIONS_REF_PREFIX)) {
    const definitionName = ref.slice(DEFINITIONS_REF_PREFIX.length);
    return resolveEnum(definitions[definitionName], definitions, definitionName);
  }
  const values = property.enum;
  if (Array.isArray(values) && values.length > 0 && values.every((value) => typeof value === 'string')) {
    return { name, values };
  }
  const branches: MutationConfirmationSchemaProperty[] = property.anyOf ?? property.oneOf ?? [];
  return branches.map((branch) => resolveEnum(branch, definitions, name)).find(Boolean);
};

const resolveLabel = (
  name: string,
  tool: string,
  classification: MutationConfirmationFieldClassification | undefined,
  property: MutationConfirmationSchemaProperty | undefined,
  translate: ConfirmationTranslate | undefined,
): string => {
  const fallback = resolveFieldLabel(name, classification, property);
  return translate ? translateFieldLabel(translate, tool, name, fallback) : fallback;
};

const resolveFieldLabel = (
  name: string,
  classification: MutationConfirmationFieldClassification | undefined,
  property: MutationConfirmationSchemaProperty | undefined,
): string => {
  const label = classification?.label?.trim();
  if (label) return label;
  const title = typeof property?.title === 'string' ? property.title.trim() : '';
  if (title) return title;
  return humanizeFieldName(name) || name;
};

const humanizeFieldName = (name: string): string =>
  name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const resolveSchemaType = (property: MutationConfirmationSchemaProperty | undefined): string | undefined => {
  const type = resolveSchemaBranch(property)?.type;
  if (typeof type === 'string') return type;
  return Array.isArray(type) ? type.find((entry) => entry !== 'null') : undefined;
};

export const isStringArraySchema = (property: MutationConfirmationSchemaProperty | undefined): boolean => {
  const branch = resolveSchemaBranch(property);
  if (resolveSchemaType(branch) !== 'array') return false;
  const items = branch?.items;
  return items === undefined || (isSchemaProperty(items) && resolveSchemaType(items) === 'string');
};

const resolveSchemaBranch = (
  property: MutationConfirmationSchemaProperty | undefined,
): MutationConfirmationSchemaProperty | undefined => {
  if (!property) return undefined;
  if (property.type !== undefined) return property;
  const branches: MutationConfirmationSchemaProperty[] = property.anyOf ?? property.oneOf ?? [];
  return branches.map((branch) => resolveSchemaBranch(branch)).find((branch) => branch && branch.type !== 'null');
};

interface UnproposedOptionalFieldArgs {
  name: string;
  properties: Record<string, MutationConfirmationSchemaProperty>;
  required: Set<string>;
  classification: MutationConfirmationFieldClassification | undefined;
  value: unknown;
}

// An optional schema property the model left unset is not rendered and not submitted: bound as an
// empty value it would override the omission the tool relies on to leave that part untouched.
// Sensitive fields are never model-proposed, so they always render.
const isUnproposedOptionalField = ({
  name,
  properties,
  required,
  classification,
  value,
}: UnproposedOptionalFieldArgs): boolean => {
  if (value !== null && value !== undefined) return false;
  if (required.has(name) || !(name in properties)) return false;
  return !classification?.sensitive;
};

const isSchemaProperty = (value: unknown): value is MutationConfirmationSchemaProperty =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isBooleanField = (field: CardField): boolean =>
  field.schemaType === 'boolean' || typeof field.value === 'boolean';

export const formatDisplayFieldValue = (field: CardField): string | null =>
  DISPLAY_VALUE_FORMATTERS[field.display ?? '']?.(field.value) ??
  DISPLAY_VALUE_FORMATTERS[field.name]?.(field.value) ??
  formatEnumFieldValue(field);

/** The proposed value's label, for an enum input shown without a surface. */
const formatEnumFieldValue = (field: CardField): string | null => {
  if (!field.enumOptions || typeof field.value !== 'string') return null;
  return field.enumOptions.find(({ value }) => value === field.value)?.label ?? field.value;
};

export const formatFiltersValue = (value: unknown): string =>
  (Array.isArray(value) ? value : [])
    .map((entry) => {
      try {
        return PersonFilterHelper.createInstance(entry as PersonFilter)?.getTooltip();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('confirmationFields: failed to format a geo/person filter', entry, error);
        return undefined;
      }
    })
    .filter((text): text is string => !!text)
    .join('\n');

/**
 * A Matchback base or comparison list, by audience name and selected ranges.
 *
 * Mirrors the agent's `_describe_matchback_selection` (a2ui/surfaces.py). The audience id is
 * deliberately never read: the generic structured fallback would print the whole object.
 */
export const formatMatchbackSelectionValue = (value: unknown): string => {
  const labels = matchbackSelectionLabels(value);
  return labels.length ? labels.join('\n') : 'None';
};

/** One readable label per selection: `Spring Pulse (Jan 01, 2026 - Jan 07, 2026)`. */
export const matchbackSelectionLabels = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).map(describeMatchbackSelection).filter((line): line is string => !!line);

const describeMatchbackSelection = (selection: unknown): string | null => {
  if (typeof selection !== 'object' || selection === null) return null;
  const { audience_name: audienceName, date_ranges: dateRanges } = selection as {
    audience_name?: string;
    date_ranges?: unknown;
  };
  if (!audienceName) return null;
  const described = (Array.isArray(dateRanges) ? dateRanges : [])
    .map(describeMatchbackDateRange)
    .filter((text): text is string => !!text);
  return described.length ? `${audienceName} (${described.join(', ')})` : audienceName;
};

const describeMatchbackDateRange = (entry: unknown): string | null => {
  if (typeof entry !== 'object' || entry === null) return null;
  const { from, to } = entry as { from?: unknown; to?: unknown };
  const start = formatMatchbackDate(from);
  const end = formatMatchbackDate(to);
  if (!start || !end) return start ?? end;
  return start === end ? start : `${start} - ${end}`;
};

/** `2026-09-15` or a full timestamp to `Sep 15, 2026`; anything unparseable passes through. */
const formatMatchbackDate = (raw: unknown): string | null => {
  if (typeof raw !== 'string' || !raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return moment.utc(parsed).format('MMM DD, YYYY');
};

const DISPLAY_VALUE_FORMATTERS: Record<string, (value: unknown) => string> = {
  pulseTopics: (value) =>
    (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [])
      .map(pulseTopicOptionLabel)
      .join('\n'),
  filters: formatFiltersValue,
  matchbackSelection: formatMatchbackSelectionValue,
};

export const isStructuredField = (field: CardField): boolean =>
  field.value !== null && field.value !== undefined && typeof field.value === 'object';

export const coerceFieldValue = (raw: unknown, field: CardField): unknown => {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw !== 'string') return raw;
  if (raw === '') return field.required ? '' : null;
  if (isNumericField(field)) {
    return isNumericValueValid(raw, field) ? Number(raw) : raw;
  }
  return raw;
};

export const isNumericField = (field: CardField): boolean =>
  field.schemaType === 'integer' || field.schemaType === 'number';

export const isNumericValueValid = (raw: string, field: CardField): boolean => {
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return false;
  return field.schemaType !== 'integer' || Number.isInteger(parsed);
};
